import { useEffect } from "react";
import { ArrowLeft } from "@phosphor-icons/react";
import { LuminaLogo, PrimePillarWordmark } from "../LandingPage";

// Company of record — single source of truth, reused in the footer too.
export const COMPANY = {
  name: "Lulo Software LLC",
  product: "Prime Pillar",
  addressLines: ["30 N Gould St, Ste R", "Sheridan, WY 82801, USA"],
  email: "support@lulosoftware.com",
  effective: "June 1, 2026",
};

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg sm:text-xl font-semibold text-[#0E0E0E] tracking-tight">{title}</h2>
    <div className="space-y-3 text-[15px] leading-relaxed text-[#444048]">{children}</div>
  </section>
);

const TermsBody = () => (
  <>
    <Section title="1. Who we are">
      <p>
        {COMPANY.product} is a software-as-a-service product operated by {COMPANY.name},
        a Wyoming limited liability company located at {COMPANY.addressLines.join(", ")}.
        These Terms of Service ("Terms") govern your access to and use of the Prime Pillar
        website, applications, and APIs (collectively, the "Service").
      </p>
      <p>By creating an account or using the Service you agree to these Terms.</p>
    </Section>
    <Section title="2. What the Service provides">
      <p>
        Prime Pillar surfaces market research, demand signals, and opportunity briefs
        aggregated from publicly available sources and our own analysis. The information is
        provided for research and educational purposes only. It is not investment, legal,
        tax, or financial advice. You are solely responsible for any business decision you
        make based on the Service.
      </p>
      <p>
        We work hard to ground every claim in a real source and to flag anything we could
        not verify, but we do not guarantee that any signal, score, brief, or projection is
        accurate, complete, or fit for a particular purpose.
      </p>
    </Section>
    <Section title="3. Accounts">
      <p>
        You must provide accurate information when registering and keep your credentials
        secure. You are responsible for all activity under your account. You must be at
        least 18 years old to use the Service.
      </p>
    </Section>
    <Section title="4. Subscriptions & billing">
      <p>
        Paid plans are billed in advance through our payment processor on a recurring basis
        until cancelled. You can cancel at any time; access continues until the end of the
        current billing period. Except where required by law, fees already paid are
        non-refundable.
      </p>
    </Section>
    <Section title="5. Acceptable use">
      <p>
        You agree not to resell, scrape, or redistribute Service data in bulk, reverse
        engineer the Service, misuse our APIs, or use the Service to violate any law or the
        rights of others. We may suspend or terminate accounts that abuse the Service.
      </p>
    </Section>
    <Section title="6. Intellectual property">
      <p>
        The Service, including its software, design, and original content, is owned by
        {" "}{COMPANY.name} and protected by intellectual-property laws. Source data remains
        the property of its respective owners. We grant you a limited, non-exclusive,
        non-transferable license to use the Service for your internal business purposes.
      </p>
    </Section>
    <Section title="7. Third-party data sources">
      <p>
        The Service aggregates and analyzes publicly available information from sources such
        as community discussions, product reviews, search-volume providers, advertising
        libraries, and supplier directories. We do not claim ownership of that underlying
        data, we link back to original sources wherever possible, and we present it under
        fair-use principles for the purpose of market research. If you believe content
        surfaced by the Service infringes your rights, contact us at{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-violet-700 hover:underline">{COMPANY.email}</a>{" "}
        and we will review it promptly.
      </p>
    </Section>
    <Section title="8. Service availability & changes">
      <p>
        We aim for high availability but do not guarantee the Service will be uninterrupted
        or error-free. Features marked as beta or experimental may change or be withdrawn at
        any time. We may modify or discontinue parts of the Service; where a change
        materially reduces the value of a paid plan, we will provide reasonable notice.
      </p>
    </Section>
    <Section title="9. Termination">
      <p>
        You may stop using the Service and delete your account at any time. We may suspend
        or terminate your access if you materially breach these Terms, create risk or legal
        exposure for us, or where required by law. Sections that by their nature should
        survive termination (including intellectual property, disclaimers, and limitation of
        liability) survive.
      </p>
    </Section>
    <Section title="10. Disclaimers & limitation of liability">
      <p>
        The Service is provided "as is" without warranties of any kind, express or implied,
        including merchantability, fitness for a particular purpose, and non-infringement.
        To the maximum extent permitted by law, {COMPANY.name} will not be liable for any
        indirect, incidental, special, or consequential damages, loss of profits, or loss of
        data, and our total liability for any claim is limited to the amount you paid us in
        the twelve months preceding the claim.
      </p>
    </Section>
    <Section title="11. Governing law">
      <p>
        These Terms are governed by the laws of the State of Wyoming, USA, without regard to
        conflict-of-law principles. Any dispute will be resolved in the state or federal
        courts located in Wyoming, and you consent to their jurisdiction.
      </p>
    </Section>
    <Section title="12. Changes & contact">
      <p>
        We may update these Terms from time to time; material changes will be posted here
        with a new effective date. Your continued use of the Service after changes take
        effect constitutes acceptance. Questions? Contact us at{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-violet-700 hover:underline">{COMPANY.email}</a>.
      </p>
    </Section>
  </>
);

const PrivacyBody = () => (
  <>
    <Section title="1. Overview">
      <p>
        This Privacy Policy explains how {COMPANY.name} ("we", "us") collects, uses, and
        protects information when you use {COMPANY.product}. We are located at{" "}
        {COMPANY.addressLines.join(", ")}.
      </p>
    </Section>
    <Section title="2. Information we collect">
      <p>
        <strong>Account data</strong> — your email and authentication details when you sign
        up. <strong>Usage data</strong> — pages viewed, filters used, and feature
        interactions, used to improve the product. <strong>Payment data</strong> — handled
        by our payment processor; we never store full card numbers.
      </p>
    </Section>
    <Section title="3. Cookies">
      <p>
        We use strictly-necessary functional cookies to keep you signed in and remember your
        preferences. With your consent, we also use analytics cookies to understand how the
        Service is used. You can decline analytics cookies at any time via the cookie banner.
      </p>
    </Section>
    <Section title="4. How we use your information">
      <p>
        To provide and secure the Service, process payments, respond to support requests,
        send essential account communications, and improve our features. We do not sell your
        personal information.
      </p>
    </Section>
    <Section title="5. Data sharing & subprocessors">
      <p>
        We share data only with service providers who help us run the Service, under
        contracts that require them to protect it, or when required by law. Our current
        subprocessors include: our cloud hosting provider (application and database
        infrastructure), Stripe (payment processing), PostHog (privacy-focused product
        analytics), and our transactional email provider (account and support emails).
        We do not sell or rent your personal information to anyone.
      </p>
    </Section>
    <Section title="6. Legal bases (GDPR)">
      <p>
        Where the EU/UK GDPR applies, we process your data on the following bases:
        <strong> performance of a contract</strong> (providing the Service you signed up
        for), <strong>legitimate interests</strong> (securing and improving the Service),
        <strong> consent</strong> (analytics cookies and optional communications), and
        <strong> legal obligation</strong> (tax and accounting records).
      </p>
    </Section>
    <Section title="7. Your rights (GDPR & CCPA)">
      <p>
        Depending on your location, you may have rights to access, correct, export, or delete
        your personal data, object to or restrict certain processing, and withdraw consent at
        any time. California residents additionally have the right to know what personal
        information is collected and to non-discrimination for exercising their rights.
        To exercise any of these rights, email{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-violet-700 hover:underline">{COMPANY.email}</a>{" "}
        — we respond within 30 days. You may also lodge a complaint with your local data
        protection authority.
      </p>
    </Section>
    <Section title="8. International transfers">
      <p>
        We are a US company and process data in the United States. Where data of EU/UK
        residents is transferred, we rely on recognized safeguards such as Standard
        Contractual Clauses with our subprocessors.
      </p>
    </Section>
    <Section title="9. Data retention & security">
      <p>
        We retain personal data for as long as your account is active or as needed to provide
        the Service and meet legal obligations. When you delete your account, personal data is
        removed or anonymized within 30 days, except records we must keep by law. We apply
        reasonable technical and organizational measures — encrypted transport (TLS), hashed
        passwords, least-privilege access — and will notify affected users without undue delay
        in the event of a data breach involving personal data.
      </p>
    </Section>
    <Section title="10. Children's privacy">
      <p>
        The Service is not directed to children under 16 and we do not knowingly collect
        personal data from them. If you believe a child has provided us personal data,
        contact us and we will delete it.
      </p>
    </Section>
    <Section title="11. Changes & contact">
      <p>
        We may update this policy from time to time; material changes will be posted here
        with a new effective date. Questions about this policy? Contact {COMPANY.name} at{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-violet-700 hover:underline">{COMPANY.email}</a>{" "}
        or write to us at {COMPANY.addressLines.join(", ")}.
      </p>
    </Section>
  </>
);

// Public legal page rendered at /terms and /privacy. Self-contained shell so it
// never depends on the in-app data fetches.
const LegalPage = ({ kind = "terms" }) => {
  const isTerms = kind === "terms";
  const title = isTerms ? "Terms of Service" : "Privacy Policy";
  useEffect(() => {
    document.title = `${title} | ${COMPANY.product}`;
    const desc = isTerms
      ? `Terms of Service for ${COMPANY.product} by ${COMPANY.name} — subscriptions, acceptable use, data sources, intellectual property, disclaimers and governing law.`
      : `Privacy Policy for ${COMPANY.product} by ${COMPANY.name} — what data we collect, how we use it, cookies, third-party processors, retention and your GDPR/CCPA rights.`;
    let m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", desc);
    return () => { document.title = "Prime Pillar | Micro-SaaS Opportunity Scanner"; };
  }, [isTerms, title]);
  return (
    <div className="min-h-screen bg-[#F1F3F8] text-[#0E0E0E] font-body" data-testid={`legal-page-${kind}`}>
      <header className="lum-glass border-b border-white/8 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-14 sm:h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 min-w-0" data-testid="legal-logo-home">
            <LuminaLogo size={44} testid="legal-logo" />
            <PrimePillarWordmark height={28} className="hidden sm:block" testid="legal-wordmark" />
          </a>
          <a href="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="legal-back-home">
            <ArrowLeft size={15} weight="bold" /> Back to home
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.25em] text-violet-700 mb-3">
          {COMPANY.product} · {COMPANY.name}
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{title}</h1>
        <p className="text-sm text-[#9B9498] mb-10">Effective {COMPANY.effective}</p>

        <div className="space-y-9">{isTerms ? <TermsBody /> : <PrivacyBody />}</div>

        <div className="mt-14 pt-6 border-t border-zinc-200 text-sm text-[#5A575B]">
          <div className="font-semibold text-[#0E0E0E]">{COMPANY.name}</div>
          {COMPANY.addressLines.map((l) => <div key={l}>{l}</div>)}
          <a href={`mailto:${COMPANY.email}`} className="text-violet-700 hover:underline">{COMPANY.email}</a>
          <div className="mt-6 flex gap-5 font-mono-data text-[11px] uppercase tracking-[0.25em] text-[#9B9498]">
            <a href="/terms" className="hover:text-[#0E0E0E] transition-colors" data-testid="legal-link-terms">Terms</a>
            <a href="/privacy" className="hover:text-[#0E0E0E] transition-colors" data-testid="legal-link-privacy">Privacy</a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
